// File: src/components/fans/PointsTipModal.js
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getFirestore, doc, getDoc } from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { getAnalytics, logEvent } from "firebase/analytics";
import { useProfile } from "../../context/ProfileContext";
import { getFunctions, httpsCallable } from "firebase/functions";
import toast from "react-hot-toast";
import { motion } from "framer-motion";

import { BADGE_TIERS, OFFLINE_DOWNLOAD_THRESHOLD } from "../../config/constants";

// Import celebration modals
import FanUnlockModal from "./FanUnlockModal";
import DownloadUnlockModal from "./DownloadUnlockModal";

// Import official boost icon assets
import tipIcon from "../../assets/tip_icon.png";
import tipWhiteIcon from "../../assets/tip_white.png";

const POINT_PRESETS = [100, 500, 1000, 5000];

const ARTIST_MILESTONES = [
  { name: BADGE_TIERS.FAN.name, value: BADGE_TIERS.FAN.minPoints, color: "#22c55e" },
  { name: BADGE_TIERS.SUPAFAN.name, value: BADGE_TIERS.SUPAFAN.minPoints, color: "#3b82f6" },
  { name: BADGE_TIERS.ULTRAFAN.name, value: BADGE_TIERS.ULTRAFAN.minPoints, color: "#ff00cc" }
];

const PointsTipModal = ({ isOpen, onClose, songToTip: currentSong, isDarkMode = true }) => {
  const navigate = useNavigate();
  const [frozenSong, setFrozenSong] = useState(null);
  const [step, setStep] = useState('input');
  
  const [isProcessing, setIsProcessing] = useState(false);
  const [tipAmount, setTipAmount] = useState(100);
  
  const [cumulativeArtistTip, setCumulativeArtistTip] = useState(0);
  const [cumulativeSongTip, setCumulativeSongTip] = useState(0);
  const [isAlreadyUnlocked, setIsAlreadyUnlocked] = useState(false);
  const [isArtistOwner, setIsArtistOwner] = useState(false);
  const [error, setError] = useState("");

  const [successData, setSuccessData] = useState({
    pointsTipped: 0,
    newBalance: 0,
    badgeUnlocked: null,
    unlockedDownload: false,
  });

  // Modal display states for sequential chaining
  const [isFanUnlockModalOpen, setIsFanUnlockModalOpen] = useState(false);
  const [isDownloadUnlockModalOpen, setIsDownloadUnlockModalOpen] = useState(false);
  
  const [pendingNavigationUrl, setPendingNavigationUrl] = useState(null);

  const auth = getAuth();
  const user = auth.currentUser;
  const db = getFirestore();

  const { userProfile, activeProfile } = useProfile();
  const userPointsBalance = userProfile?.points || 0;

  useEffect(() => {
    if (!isOpen) {
      setStep('input');
      setError("");
      setIsArtistOwner(false);
      setFrozenSong(null);
      setPendingNavigationUrl(null);
      return;
    }

    if (currentSong && !frozenSong) {
      setFrozenSong({ ...currentSong });
    }
  }, [isOpen, currentSong]);

  useEffect(() => {
    if (!isOpen || !frozenSong) return;

    const fetchTipAndOwnerData = async () => {
      if (!user || !frozenSong?.artistId) return;

      try {
        // 1. Fetch the Artist document to perform robust ownership detection
        const artistRef = doc(db, "artist_accounts", frozenSong.artistId);
        const artistSnap = await getDoc(artistRef);

        if (artistSnap.exists()) {
          const artistData = artistSnap.data();
          const cleanUserEmail = user.email?.trim().toLowerCase();
          const cleanArtistEmail = artistData.email?.trim().toLowerCase();

          // Thorough Multi-Factor Ownership Check
          const ownsAccount = Boolean(
            // Direct UID matches on common fields
            artistData.createdBy === user.uid ||
            artistData.userId === user.uid ||
            artistData.ownerUid === user.uid ||
            // Profile context references
            userProfile?.artistAccountId === frozenSong.artistId ||
            activeProfile?.id === frozenSong.artistId ||
            (Array.isArray(userProfile?.associatedArtistIds) && userProfile.associatedArtistIds.includes(frozenSong.artistId)) ||
            // Email matches (direct or registered within associatedEmails)
            (cleanUserEmail && cleanArtistEmail && cleanUserEmail === cleanArtistEmail) ||
            (Array.isArray(userProfile?.associatedEmails) && cleanArtistEmail && userProfile.associatedEmails.map(e => e.toLowerCase()).includes(cleanArtistEmail))
          );

          setIsArtistOwner(ownsAccount);
        }

        // 2. Fetch Cumulative Artist and Song Boosts
        const profileLookupId = activeProfile?.id || user.uid;
        if (profileLookupId) {
          const artistTipRef = doc(db, "users", profileLookupId, "artistsTipped", frozenSong.artistId);
          const artistTipSnap = await getDoc(artistTipRef);
          setCumulativeArtistTip(artistTipSnap.exists() ? (artistTipSnap.data()?.totalPointsTipped || 0) : 0);

          if (frozenSong?.id) {
            const songTipRef = doc(db, "users", profileLookupId, "tipsGiven", frozenSong.id);
            const songTipSnap = await getDoc(songTipRef);
            const songPoints = songTipSnap.exists() ? (songTipSnap.data()?.totalPointsTipped || 0) : 0;
            
            setCumulativeSongTip(songPoints);
            setIsAlreadyUnlocked(songPoints >= OFFLINE_DOWNLOAD_THRESHOLD);
          }
        }
      } catch (err) {
        console.error("Error fetching boost and ownership data:", err);
      }
    };

    fetchTipAndOwnerData();
  }, [isOpen, frozenSong, user, activeProfile, userProfile, db]);

  const handleTip = async () => {
    if (!frozenSong) return;
    setIsProcessing(true);
    setError("");

    if (!user) {
      toast.error("You must be logged in to boost with points.");
      setIsProcessing(false);
      return;
    }

    const pointsToTip = parseInt(tipAmount, 10);

    if (isNaN(pointsToTip) || pointsToTip <= 0) {
      setError("Please enter a valid amount of points to boost.");
      setIsProcessing(false);
      return;
    }

    if (pointsToTip > userPointsBalance) {
      setError(`Insufficient balance. You have ${userPointsBalance.toLocaleString()} points.`);
      setIsProcessing(false);
      return;
    }

    try {
      const functions = getFunctions();
      const tipArtistPoints = httpsCallable(functions, "tipArtistPoints");

      const response = await tipArtistPoints({
        artistId: frozenSong.artistId,
        songId: frozenSong.id || null,
        pointsAmount: pointsToTip,
      });

      const data = response.data || {};

      const analytics = getAnalytics();
      logEvent(analytics, 'boost_completed', {
        song_id: frozenSong.id || 'N/A',
        artist_id: frozenSong.artistId,
        boost_amount_points: pointsToTip,
        currency: 'POINTS',
        is_owner_preview: isArtistOwner
      });

      setSuccessData({
        pointsTipped: pointsToTip,
        newBalance: data.newBalance !== undefined ? data.newBalance : (userPointsBalance - pointsToTip),
        badgeUnlocked: data.badgeUnlocked || null,
        unlockedDownload: data.unlockedDownload || false,
      });

      setStep('success');

    } catch (err) {
      console.error("Boost points error:", err);
      const errorMessage = err.message || "An unexpected error occurred.";
      toast.error(`Boost failed: ${errorMessage}`);
      setError(errorMessage);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCloseSuccess = () => {
    onClose();

    if (successData.badgeUnlocked) {
      setIsFanUnlockModalOpen(true);
    } else if (successData.unlockedDownload) {
      setIsDownloadUnlockModalOpen(true);
    }
  };

  const handleExploreExclusiveClick = (artistId) => {
    setIsFanUnlockModalOpen(false);
    const targetUrl = `/profile/${artistId}?tab=press`;

    if (successData.unlockedDownload) {
      setPendingNavigationUrl(targetUrl);
      setTimeout(() => {
        setIsDownloadUnlockModalOpen(true);
      }, 300);
    } else {
      navigate(targetUrl);
    }
  };

  const handleFanUnlockModalClose = () => {
    setIsFanUnlockModalOpen(false);

    if (successData.unlockedDownload) {
      setTimeout(() => {
        setIsDownloadUnlockModalOpen(true);
      }, 300);
    }
  };

  const handleDownloadModalClose = () => {
    setIsDownloadUnlockModalOpen(false);

    if (pendingNavigationUrl) {
      navigate(pendingNavigationUrl);
      setPendingNavigationUrl(null);
    }
  };

  if (!isOpen && !isFanUnlockModalOpen && !isDownloadUnlockModalOpen) return null;

  const targetSong = frozenSong || currentSong;
  const currentTipInputPoints = parseInt(tipAmount, 10) || 0;
  
  const totalPotentialSongPoints = cumulativeSongTip + currentTipInputPoints;
  const isWillUnlockSongWithTip = !isAlreadyUnlocked && totalPotentialSongPoints >= OFFLINE_DOWNLOAD_THRESHOLD;
  const songPointsRemaining = Math.max(OFFLINE_DOWNLOAD_THRESHOLD - totalPotentialSongPoints, 0);

  const totalPotentialArtistPoints = cumulativeArtistTip + currentTipInputPoints;
  
  const previousTier = [...ARTIST_MILESTONES].reverse().find(m => cumulativeArtistTip >= m.value) || { name: "Supporter", value: 0, color: "#94a3b8" };
  const targetAchievedTier = [...ARTIST_MILESTONES].reverse().find(m => totalPotentialArtistPoints >= m.value) || null;
  const isUnlockingNewBadge = targetAchievedTier && targetAchievedTier.value > previousTier.value;
  const nextArtistMilestone = ARTIST_MILESTONES.find(m => totalPotentialArtistPoints < m.value) || null;

  let progressPercent = 0;
  if (isUnlockingNewBadge) {
    progressPercent = 100;
  } else if (nextArtistMilestone) {
    const prevMilestoneVal = previousTier.value;
    const range = nextArtistMilestone.value - prevMilestoneVal;
    const progress = totalPotentialArtistPoints - prevMilestoneVal;
    progressPercent = Math.min(Math.max((progress / range) * 100, 0), 100).toFixed(0);
  } else {
    progressPercent = 100;
  }

  return (
    <>
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/70 z-[300] flex items-center justify-center p-4 backdrop-blur-sm"
          onClick={onClose}
        >
          <div 
            className={`rounded-3xl shadow-2xl p-6 md:p-8 w-full max-w-sm border relative transition-colors ${
              isDarkMode ? "bg-zinc-900 border-zinc-800 text-white" : "bg-white border-slate-100 text-slate-900"
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button 
              onClick={onClose} 
              className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 dark:hover:text-white text-xl leading-none transition-colors"
              aria-label="Close modal"
            >
              &times;
            </button>

            {/* STEP 1: INPUT FORM */}
            {step === 'input' && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <h2 className="text-xl font-black italic tracking-tight mb-3 text-center">
                  Boost Track With Points
                </h2>

                {targetSong && (
                  <div className="mb-4 p-2.5 bg-slate-100 dark:bg-zinc-800/80 border border-slate-200 dark:border-zinc-700/60 rounded-2xl flex items-center gap-3">
                    <img 
                      src={targetSong.albumArtworkURL || targetSong.artworkUrl || "/default-artwork.png"} 
                      alt={targetSong.title || "Track Artwork"} 
                      className="w-12 h-12 rounded-xl object-cover flex-shrink-0 border border-black/10 shadow-sm"
                    />
                    
                    <div className="min-w-0 flex-1">
                      <h4 className="text-xs font-black truncate text-slate-900 dark:text-white leading-tight">
                        {targetSong.title || "Selected Song"}
                      </h4>
                      <p className="text-[11px] font-bold text-slate-500 dark:text-zinc-400 truncate mt-0.5">
                        {targetSong.artistName || "Artist"}
                      </p>
                    </div>
                  </div>
                )}

                {/* 🌟 OWNER PREVIEW BANNER */}
                {isArtistOwner && (
                  <div className="mb-4 p-3.5 bg-purple-100 dark:bg-purple-900/40 border border-purple-300 dark:border-purple-500/40 rounded-2xl text-left space-y-1.5">
                    <div className="text-purple-900 dark:text-purple-300 font-black text-xs uppercase tracking-wider">
                      PREVIEW FAN VIEW
                    </div>
                    <ul className="text-[10px] text-purple-800 dark:text-purple-200/90 font-semibold space-y-1 list-disc list-inside leading-snug">
                      <li>Your account will not appear on public fan leaderboards.</li>
                      <li>You gain access to the full &quot;Fan Experience&quot; for preview (Fan-Only content &amp; notifications).</li>
                      <li>Boost Points will be credited to your artist account.</li>
                    </ul>
                  </div>
                )}

                <div className="space-y-4">
                  <div className="p-3.5 bg-slate-50 dark:bg-zinc-800/50 border border-slate-200 dark:border-zinc-700/60 rounded-2xl space-y-2 text-xs font-bold">
                    <div className="flex justify-between items-center">
                      <span className="text-slate-500 dark:text-zinc-400 uppercase tracking-wider text-[10px]">Your Balance:</span>
                      
                      <span className="flex items-center gap-1.5 text-sm font-black text-[#ff00cc]">
                        <img 
                          src={isDarkMode ? tipWhiteIcon : tipIcon} 
                          alt="Points" 
                          className="w-4 h-4 object-contain select-none" 
                        />
                        {userPointsBalance.toLocaleString()} pts
                      </span>
                    </div>

                    <div className="pt-2 border-t border-slate-200 dark:border-zinc-700/80 text-[11px] text-slate-600 dark:text-zinc-300">
                      Your Boosts on this track: <span className="font-bold text-slate-900 dark:text-white">{cumulativeSongTip.toLocaleString()} pts</span>
                    </div>

                    <div className="text-[11px] font-bold">
                      {isAlreadyUnlocked ? (
                        <span className="text-green-500">Offline Playback Unlocked!</span>
                      ) : isWillUnlockSongWithTip ? (
                        <span className="text-green-500 dark:text-green-400 font-black animate-pulse">
                          This Boost unlocks offline playback!
                        </span>
                      ) : (
                        <span className="text-slate-500 dark:text-slate-400">
                          Just <span className="text-slate-900 dark:text-white font-bold">{songPointsRemaining.toLocaleString()}</span> points more to unlock Offline Playback!
                        </span>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-[10px] font-black uppercase tracking-wider text-slate-400 dark:text-zinc-500 mb-2">
                      Quick Select
                    </label>
                    <div className="grid grid-cols-4 gap-2">
                      {POINT_PRESETS.map((preset) => (
                        <button
                          key={preset}
                          type="button"
                          onClick={() => setTipAmount(preset)}
                          className={`py-2 text-xs font-black rounded-xl border transition-all ${
                            tipAmount === preset
                              ? "bg-[#ff00cc] text-white border-[#ff00cc] shadow-md scale-102"
                              : "bg-slate-100 dark:bg-zinc-800 text-slate-700 dark:text-zinc-300 border-slate-200 dark:border-zinc-700 hover:bg-slate-200 dark:hover:bg-zinc-700"
                          }`}
                        >
                          +{preset}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label htmlFor="tipAmountPoints" className="block text-[10px] font-black uppercase tracking-wider text-slate-400 dark:text-zinc-500 mb-1.5">
                      Custom Point Amount
                    </label>
                    <input
                      id="tipAmountPoints"
                      type="number"
                      value={tipAmount}
                      min="1"
                      step="1"
                      onChange={(e) => setTipAmount(e.target.value)}
                      className="w-full p-3.5 bg-slate-50 dark:bg-zinc-800 border border-slate-300 dark:border-zinc-700 rounded-2xl text-sm font-black focus:ring-2 focus:ring-[#ff00cc] outline-none"
                    />
                  </div>

                  {error && <div className="text-xs font-bold text-red-500 animate-pulse">{error}</div>}

                  <button
                    onClick={handleTip}
                    disabled={isProcessing || userPointsBalance < currentTipInputPoints}
                    className="w-full bg-[#ff00cc] hover:bg-[#e000b4] text-white py-3.5 px-5 rounded-xl font-black uppercase text-xs tracking-widest shadow-xl transition-all active:scale-98 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {isProcessing 
                      ? "Processing..." 
                      : isArtistOwner 
                        ? `Confirm Self-Boost (${currentTipInputPoints.toLocaleString()} Pts)` 
                        : `Confirm ${currentTipInputPoints.toLocaleString()} Point Boost`
                    }
                  </button>

                  <div className="pt-4 border-t border-slate-100 dark:border-zinc-800 mt-2">
                    <p className="text-[11px] text-slate-600 dark:text-zinc-300 text-center font-semibold leading-normal">
                      {isUnlockingNewBadge ? (
                        <span className="font-black text-[#ff00cc] animate-pulse">
                          This Boost unlocks {targetAchievedTier.name} status!
                        </span>
                      ) : nextArtistMilestone ? (
                        <>
                          Just <span className="font-bold text-slate-900 dark:text-white">{(nextArtistMilestone.value - totalPotentialArtistPoints).toLocaleString()}</span> more points to reach <span className="font-bold">{nextArtistMilestone.name}</span> status!
                        </>
                      ) : (
                        <span className="font-black text-[#ff00cc]">ULTRAFAN STATUS ACHIEVED!</span>
                      )}
                    </p>
                    
                    <div className="w-full bg-slate-100 dark:bg-zinc-800 rounded-full h-2 mt-2 overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-500"
                        style={{ 
                          width: `${progressPercent}%`, 
                          backgroundColor: (targetAchievedTier || previousTier).color || "#ff00cc"
                        }}
                      />
                    </div>
                    <div className="flex justify-between text-[9px] font-black tracking-widest uppercase mt-1">
                      <span style={{ color: (targetAchievedTier || previousTier).color }}>
                        Status: {(targetAchievedTier || previousTier).name}
                      </span>
                      <span className="text-slate-400">{progressPercent}% complete</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {/* STEP 2: RECEIPT VIEW */}
            {step === 'success' && (
              <motion.div 
                initial={{ scale: 0.9, opacity: 0 }} 
                animate={{ scale: 1, opacity: 1 }}
                className="text-center space-y-5 py-2"
              >
                <div>
                  <h3 className="text-xl font-black italic uppercase tracking-tight text-green-500">
                    Boost Successful!
                  </h3>
                  <p className="text-slate-400 text-xs font-bold uppercase tracking-widest mt-1">
                    Thanks For Backing This Music
                  </p>
                </div>

                {targetSong && (
                  <div className="p-2.5 bg-slate-100 dark:bg-zinc-800/80 border border-slate-200 dark:border-zinc-700/60 rounded-2xl flex items-center gap-3 text-left">
                    <img 
                      src={targetSong.albumArtworkURL || targetSong.artworkUrl || "/default-artwork.png"} 
                      alt={targetSong.title || "Track Artwork"} 
                      className="w-10 h-10 rounded-xl object-cover flex-shrink-0"
                    />
                    <div className="min-w-0 flex-1">
                      <h4 className="text-xs font-black truncate text-slate-900 dark:text-white">
                        {targetSong.title}
                      </h4>
                      <p className="text-[10px] font-bold text-slate-500 dark:text-zinc-400 truncate">
                        {targetSong.artistName}
                      </p>
                    </div>
                  </div>
                )}

                <div className="p-4 rounded-2xl bg-slate-50 dark:bg-zinc-800/60 border border-slate-200 dark:border-zinc-700/80 space-y-2 text-xs font-semibold">
                  <div className="flex justify-between items-center">
                    <span className="text-slate-400">Points Transferred:</span>
                    <span className="font-black text-base text-[#ff00cc]">
                      +{successData.pointsTipped.toLocaleString()} pts
                    </span>
                  </div>
                  <div className="flex justify-between items-center border-t border-slate-200 dark:border-zinc-700 pt-2">
                    <span className="text-slate-400">New Balance:</span>
                    
                    <span className="flex items-center gap-1.5 font-black text-slate-800 dark:text-white">
                      <img 
                        src={isDarkMode ? tipWhiteIcon : tipIcon} 
                        alt="Points" 
                        className="w-4 h-4 object-contain select-none" 
                      />
                      {successData.newBalance.toLocaleString()} pts
                    </span>
                  </div>
                </div>

                <button
                  onClick={handleCloseSuccess}
                  className="w-full py-3.5 px-4 rounded-xl font-black uppercase tracking-wider text-xs bg-[#ff00cc] hover:bg-[#e000b4] text-white transition-all shadow-lg active:scale-95"
                >
                  Done
                </button>
              </motion.div>
            )}

          </div>
        </div>
      )}

      {/* SEQUENTIAL MODAL 1: FAN UNLOCK CELEBRATION */}
      <FanUnlockModal
        isOpen={isFanUnlockModalOpen}
        onClose={handleFanUnlockModalClose}
        onExploreExclusive={handleExploreExclusiveClick}
        artistName={targetSong?.artistName || "Artist"}
        artistId={targetSong?.artistId}
        artistPhotoURL={targetSong?.artistPhotoURL || targetSong?.artistPhoto}
        badgeTier={successData.badgeUnlocked || "FAN"}
      />

      {/* SEQUENTIAL MODAL 2: DOWNLOAD UNLOCK CELEBRATION */}
      <DownloadUnlockModal
        isOpen={isDownloadUnlockModalOpen}
        onClose={handleDownloadModalClose}
        song={targetSong}
      />
    </>
  );
};

export default PointsTipModal;